import { useState, useEffect, useCallback } from "react";
import { Tag, X, CheckCircle, AlertCircle, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  validateCoupon,
  getAutoPromotions,
  type CouponDiscountResult,
  type ProductType,
} from "@/api/lms/coupons";

interface CouponInputProps {
  productType: ProductType;
  productId: string;
  originalPrice: number;
  currency?: string;
  onCouponApplied?: (result: CouponDiscountResult) => void;
  onCouponCleared?: () => void;
  className?: string;
}

export function CouponInput({
  productType,
  productId,
  originalPrice,
  currency = "BDT",
  onCouponApplied,
  onCouponCleared,
  className,
}: CouponInputProps) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<CouponDiscountResult | null>(null);
  const [autoPromos, setAutoPromos] = useState<CouponDiscountResult[]>([]);
  const [promosLoading, setPromosLoading] = useState(false);

  // Fetch auto-promotions when component mounts / price changes
  useEffect(() => {
    if (originalPrice <= 0) return;
    let cancelled = false;
    setPromosLoading(true);
    getAutoPromotions(productType, productId, originalPrice)
      .then((promos) => {
        if (!cancelled) {
          setAutoPromos(promos);
          // Auto-apply best promo if no coupon already entered
          if (promos.length > 0 && !applied) {
            const best = promos.reduce((a, b) =>
              a.discountAmount > b.discountAmount ? a : b,
            );
            setApplied(best);
            onCouponApplied?.(best);
          }
        }
      })
      .catch(() => {
        // Silently ignore — auto-promotions are optional
      })
      .finally(() => {
        if (!cancelled) setPromosLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType, productId, originalPrice]);

  const handleApply = useCallback(async () => {
    if (!code.trim()) {
      setError("Please enter a coupon code");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await validateCoupon({
        code: code.trim().toUpperCase(),
        productType,
        productId,
        originalPrice,
      });
      setApplied(result);
      onCouponApplied?.(result);
      setCode("");
    } catch (err: any) {
      const message =
        err?.body?.message ||
        err?.message ||
        "Invalid or expired coupon code";
      setError(typeof message === "string" ? message : "Invalid coupon code");
    } finally {
      setLoading(false);
    }
  }, [code, productType, productId, originalPrice, onCouponApplied]);

  const handleClear = () => {
    setApplied(null);
    setCode("");
    setError(null);
    onCouponCleared?.();
    // Recheck auto-promotions
    if (autoPromos.length > 0) {
      const best = autoPromos.reduce((a, b) =>
        a.discountAmount > b.discountAmount ? a : b,
      );
      setApplied(best);
      onCouponApplied?.(best);
    }
  };

  const fmtPrice = (n: number) =>
    `${currency} ${n.toLocaleString("en-BD", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

  return (
    <div className={cn("space-y-3", className)}>
      {/* Auto-promotion banner */}
      {autoPromos.length > 0 && !applied?.couponCode && (
        <div className="flex items-start gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2.5 text-emerald-700">
          <Sparkles size={14} className="shrink-0 mt-0.5 text-emerald-500" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold">
              Auto-promotion applied:{" "}
              <span className="text-emerald-800">{applied?.couponName ?? autoPromos[0].couponName}</span>
            </p>
            <p className="text-[11px] text-emerald-600 mt-0.5">
              Saving {fmtPrice(applied?.discountAmount ?? autoPromos[0].discountAmount)} automatically
            </p>
          </div>
        </div>
      )}

      {/* Coupon code input */}
      {!applied?.couponCode && (
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Tag
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id={`coupon-input-${productId}`}
              placeholder="Enter coupon code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                setError(null);
              }}
              onKeyDown={(e) => e.key === "Enter" && handleApply()}
              className="pl-8 h-9 text-sm font-mono uppercase tracking-wide"
              disabled={loading}
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleApply}
            disabled={loading || !code.trim()}
            className="h-9 px-4 shrink-0"
          >
            {loading ? <Loader2 size={13} className="animate-spin" /> : "Apply"}
          </Button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-1.5 text-xs text-destructive">
          <AlertCircle size={13} className="shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Applied discount card */}
      {applied && (
        <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50 p-4 space-y-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle size={14} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-xs font-bold text-emerald-800">
                  {applied.couponCode
                    ? `Code: ${applied.couponCode}`
                    : "Auto-Promotion"}
                </p>
                <p className="text-[11px] text-emerald-600">{applied.couponName}</p>
              </div>
            </div>
            <button
              onClick={handleClear}
              className="text-muted-foreground hover:text-destructive transition-colors p-1 rounded"
              aria-label="Remove coupon"
            >
              <X size={13} />
            </button>
          </div>

          {/* Order summary */}
          <div className="space-y-1.5 text-xs">
            <div className="flex justify-between text-muted-foreground">
              <span>Original Price</span>
              <span className="font-mono">{fmtPrice(applied.originalPrice)}</span>
            </div>
            <div className="flex justify-between text-emerald-700 font-semibold">
              <span className="flex items-center gap-1">
                <Tag size={11} />
                Discount
                {applied.discountType === "PERCENTAGE" &&
                  ` (${applied.discountValue}%)`}
              </span>
              <span className="font-mono">− {fmtPrice(applied.discountAmount)}</span>
            </div>
            <div className="border-t border-emerald-200 pt-1.5 flex justify-between font-bold text-sm text-foreground">
              <span>Final Price</span>
              <span className="font-mono text-emerald-700">{fmtPrice(applied.finalPrice)}</span>
            </div>
          </div>

          {/* Savings badge */}
          <div className="flex items-center justify-center">
            <span className="inline-flex items-center gap-1 bg-emerald-600 text-white text-[11px] font-bold px-3 py-1 rounded-full">
              <Sparkles size={10} />
              You save {fmtPrice(applied.savings)}!
            </span>
          </div>
        </div>
      )}

      {promosLoading && (
        <p className="text-[11px] text-muted-foreground flex items-center gap-1">
          <Loader2 size={10} className="animate-spin" /> Checking for promotions…
        </p>
      )}
    </div>
  );
}
